import React, { useState, useEffect } from 'react';
import { Save, Play, X, Tag } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { VISIBILITY_OPTIONS } from '../../../analytics-shared/constants';
import { principalsApi } from '../../../analytics-shared/api/sharesApi';

const SaveReportModal = ({ open, onClose, config, folders, onSave }) => {
  const [meta, setMeta] = useState({ name: '', folder: 'Unfiled', description: '', visibility: 'Private', tags: [], sharedGroupIds: [] });
  const [tagInput, setTagInput] = useState('');
  const [groups, setGroups] = useState([]);

  useEffect(() => {
    if (!open) return;
    principalsApi.listGroups().then((rows) => setGroups(rows || [])).catch(() => setGroups([]));
  }, [open]);

  useEffect(() => {
    if (open) {
      setMeta({
        name: config.name === 'Untitled Report' ? '' : config.name,
        folder: config.folder || 'Unfiled',
        description: config.description || '',
        visibility: config.visibility || 'Private',
        tags: config.tags || [],
        sharedGroupIds: config.sharedGroupIds || [],
      });
    }
  }, [open, config]);

  const addTag = () => {
    const t = tagInput.trim();
    if (t && !meta.tags.includes(t)) setMeta((m) => ({ ...m, tags: [...m.tags, t] }));
    setTagInput('');
  };

  const submit = (runAfter) => {
    const payload = { ...meta, name: meta.name.trim() || 'Untitled Report' };
    if (payload.visibility !== 'Groups') payload.sharedGroupIds = [];
    onSave(payload, runAfter);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Save Report</DialogTitle>
          <DialogDescription>Reports appear in your library and selected folder.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-600">Report Name *</label>
            <Input value={meta.name} onChange={(e) => setMeta((m) => ({ ...m, name: e.target.value }))}
              placeholder="e.g. Q2 Pipeline by Stage" className="mt-1" autoFocus />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600">Folder</label>
              <select value={meta.folder} onChange={(e) => setMeta((m) => ({ ...m, folder: e.target.value }))}
                className="w-full mt-1 h-10 border border-slate-200 rounded-md px-2 text-sm bg-white">
                <option value="Unfiled">Unfiled</option>
                {folders.map((f) => <option key={f.id} value={f.name}>{f.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Visibility</label>
              <select value={meta.visibility} onChange={(e) => setMeta((m) => ({ ...m, visibility: e.target.value }))}
                className="w-full mt-1 h-10 border border-slate-200 rounded-md px-2 text-sm bg-white">
                {VISIBILITY_OPTIONS.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
              <p className="text-[11px] text-slate-400 mt-1">
                {meta.visibility === 'Private' && 'Only you can see this report (plus anyone you explicitly share it with).'}
                {meta.visibility === 'Public' && 'Everyone in your organization can view this report.'}
                {meta.visibility === 'Groups' && 'Only members of the selected groups can view this report.'}
              </p>
            </div>
          </div>

          {meta.visibility === 'Groups' && (
            <div>
              <label className="text-xs font-medium text-slate-600">Share with groups</label>
              {groups.length === 0 ? (
                <p className="text-xs text-slate-400 mt-1">No groups exist yet — create them under Setup → Groups.</p>
              ) : (
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {groups.map((g) => {
                    const on = meta.sharedGroupIds.includes(g.id);
                    return (
                      <button key={g.id} type="button"
                        onClick={() => setMeta((m) => ({
                          ...m,
                          sharedGroupIds: on
                            ? m.sharedGroupIds.filter((x) => x !== g.id)
                            : [...m.sharedGroupIds, g.id],
                        }))}
                        className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                          on ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-slate-600 border-slate-200 hover:border-violet-300'
                        }`}>
                        {g.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <div>
            <label className="text-xs font-medium text-slate-600">Description</label>
            <textarea value={meta.description} onChange={(e) => setMeta((m) => ({ ...m, description: e.target.value }))}
              placeholder="What does this report show?"
              className="w-full mt-1 h-16 border border-slate-200 rounded-md p-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          </div>

          <div>
            <label className="text-xs font-medium text-slate-600 flex items-center gap-1">
              <Tag className="h-3 w-3" /> Tags
            </label>
            <div className="flex gap-1.5 mt-1">
              <Input value={tagInput} onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                placeholder="Type a tag and press Enter" className="h-9" />
              <Button type="button" variant="outline" size="sm" onClick={addTag}>Add</Button>
            </div>
            {meta.tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1.5">
                {meta.tags.map((t) => (
                  <span key={t} className="inline-flex items-center gap-1 text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full">
                    {t}
                    <button onClick={() => setMeta((m) => ({ ...m, tags: m.tags.filter((x) => x !== t) }))}>
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="outline" onClick={() => submit(true)} className="border-indigo-200 text-indigo-700">
            <Play className="h-4 w-4 mr-1.5" /> Save &amp; Run
          </Button>
          <Button onClick={() => submit(false)} className="bg-indigo-600 hover:bg-indigo-700">
            <Save className="h-4 w-4 mr-1.5" /> Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SaveReportModal;
